import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
  useCreateWarehouseLocation,
  useEditWarehouseLocation,
  useGetWarehouseLocationById,
} from "../../query/WarehouseQuery";
import { RootState } from "../../store";
import { notify } from "../../utils/toast";

const { Title, Text } = Typography;
const { Search } = Input;

const WarehouseLocation = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [warehouseCodeId, setWarehouseCodeId] = useState<number>();
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const locations = useSelector(
    (state: RootState) => state.warehouse.locations
  );
  const { data: locationData, isLoading } = useGetWarehouseLocationById(
    warehouseCodeId as number
  );
  const createWarehouseLocation = useCreateWarehouseLocation();
  const editWarehouseLocation = useEditWarehouseLocation();
  const columns = [
    {
      title: "Mã vị trí",
      dataIndex: "code",
      key: "code",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Trạng thái",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Đang hoạt động" : "Không hoạt động"}
        </Tag>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      render: (_: any, record: any) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        />
      ),
    },
  ];

  const handleLocationChange = (value: number) => {
    setWarehouseCodeId(value);
  };

  const handleEdit = (record: any) => {
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const selectedWarehouse = locations.find((loc) => loc.id === warehouseCodeId);
  const searchedLocations = locationData?.warehouseLocations.filter((loc) =>
    loc.code.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex justify-end items-center mb-4">
        <Space>
          <Search
            placeholder="Tìm kiếm vị trí..."
            allowClear
            style={{ width: 250 }}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <Select
            showSearch
            allowClear
            style={{ width: 250 }}
            placeholder="Chọn mã kho"
            optionFilterProp="children"
            onChange={handleLocationChange}
            value={warehouseCodeId}
          >
            {locations.map((wh) => (
              <Select.Option key={wh.id} value={wh.id}>
                {wh.warehouseCode}
              </Select.Option>
            ))}
          </Select>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (!warehouseCodeId) {
                notify.warning("Vui lòng chọn kho trước khi thêm vị trí");
                return;
              }
              setIsModalVisible(true);
              form.setFieldsValue({ isActive: true });
            }}
          >
            Thêm vị trí
          </Button>
        </Space>
      </div>

      {selectedWarehouse && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Card className="text-center">
            <Tooltip title="Tổng số vị trí trong kho">
              <Title
                level={2}
                style={{ color: "var(--primary-color)", margin: 0 }}
              >
                {locationData?.warehouseLocations?.length || 0}
              </Title>
              <Text type="secondary">Tổng vị trí</Text>
            </Tooltip>
          </Card>

          <Card className="text-center">
            <Tooltip title="Vị trí đang hoạt động">
              <Title level={2} style={{ color: "green", margin: 0 }}>
                {locationData?.warehouseLocations?.filter((loc) => loc.isActive)
                  ?.length || 0}
              </Title>
              <Text type="secondary">Đang hoạt động</Text>
            </Tooltip>
          </Card>

          <Card className="text-center">
            <Tooltip title="Vị trí không hoạt động">
              <Title level={2} style={{ color: "red", margin: 0 }}>
                {locationData?.warehouseLocations?.filter(
                  (loc) => !loc.isActive
                )?.length || 0}
              </Title>
              <Text type="secondary">Không hoạt động</Text>
            </Tooltip>
          </Card>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={searchedLocations || []}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: warehouseCodeId
            ? "Không có vị trí nào"
            : "Vui lòng chọn kho để xem chi tiết",
        }}
      />
      <Modal
        title={form.getFieldValue("id") ? "Sửa vị trí" : "Thêm vị trí mới"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            try {
              if (values.id) {
                await editWarehouseLocation.mutateAsync({
                  ...values,
                });
                notify.success("Cập nhật vị trí kho thành công");
              } else {
                await createWarehouseLocation.mutateAsync({
                  ...values,
                });
                notify.success("Tạo vị trí kho thành công");
              }
            } catch (error) {
              notify.error("Đã có lỗi xảy ra. Vui lòng thử lại.");
            }
            setIsModalVisible(false);
            form.resetFields();
          }}
        >
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="warehouseId" label="Mã kho">
            <Select
              showSearch
              allowClear
              placeholder="Chọn mã kho"
              value={warehouseCodeId}
              disabled={form.getFieldValue("id") ? true : false}
            >
              {locations.map((wh) => (
                <Select.Option key={wh.id} value={wh.id}>
                  {wh.warehouseCode}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="code"
            label="Mã vị trí"
            rules={[{ required: true, message: "Vui lòng nhập mã vị trí" }]}
          >
            <Input placeholder="Nhập mã vị trí" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
            rules={[{ required: true, message: "Vui lòng nhập mô tả" }]}
          >
            <Input.TextArea rows={3} placeholder="Nhập mô tả" />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="Trạng thái"
            rules={[{ required: true, message: "Vui lòng chọn trạng thái" }]}
          >
            <Select placeholder="Chọn trạng thái">
              <Select.Option value={true}>Đang hoạt động</Select.Option>
              <Select.Option value={false}>Không hoạt động</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item className="text-right mb-0">
            <Space>
              <Button
                disabled={
                  createWarehouseLocation.isPending ||
                  editWarehouseLocation.isPending
                }
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                }}
              >
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  createWarehouseLocation.isPending ||
                  editWarehouseLocation.isPending
                }
              >
                {form.getFieldValue("id") ? "Cập nhật" : "Thêm mới"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WarehouseLocation;
